/**
 * Türkçe (varsayılan dil). Diğer diller bu dosyadaki anahtarları izler.
 * Anahtar eklerken: Türkçe, İngilizce, Almanca ve Arapça birlikte eklenir (F7-17; terimler docs/I18N_GLOSSARY.md);
 * eksik anahtar CI'da hata verir (`pnpm --filter @zt/i18n missing --strict`). Vitrin metinleri `storefront.*.ts`,
 * ortak ve hata metinleri `shared.*.ts` dosyalarındadır (vitrin istemci paketi yalnızca bunları yükler).
 */
import { adminTr } from './admin.tr';
import { mobileTr } from './mobile.tr';
import { commonTr, errorsTr } from './shared.tr';
import { faz8PermissionsTr } from './faz8.tr';
import { storefrontTr } from './storefront.tr';
import { aiTr } from './ai.tr';

export const tr = {
  admin: adminTr,
  mobile: mobileTr,
  ai: aiTr.ui,
  common: commonTr,
  errors: errorsTr,
  roles: {
    owner: 'İşletme Sahibi',
    admin: 'Yönetici',
    store_manager: 'Mağaza Müdürü',
    catalog_manager: 'Katalog Sorumlusu',
    pricing_manager: 'Fiyat Sorumlusu',
    operations: 'Sipariş / Operasyon',
    warehouse: 'Depo Sorumlusu',
    finance: 'Finans / Muhasebe',
    marketing: 'Pazarlama',
    content_editor: 'İçerik Editörü',
    vet_reviewer: 'Veteriner Uzman İnceleyici',
    customer_service: 'Müşteri Hizmetleri',
    dealer_rep: 'Bayi Satış Temsilcisi',
    read_only: 'Salt Okunur / Denetçi',
  },
  roleDescriptions: {
    owner: 'Her şeye erişir. İşletmede en az bir kişi bu rolde olmalı.',
    admin: 'Sahibin yetkilerine sahiptir; sahip rolünü veremez.',
    store_manager:
      'Günlük işleri yönetir: katalog, sipariş, stok, müşteri, kampanya. Ödeme anahtarları ve rol yönetimi hariç.',
    catalog_manager:
      'Ürün, kategori, görsel ve SEO alanlarını yönetir. Fiyatı görür ama toplu fiyat değiştiremez.',
    pricing_manager: 'Fiyat listeleri, toplu fiyat ve kampanya fiyatlarını yönetir; maliyetleri görür.',
    operations: 'Siparişleri hazırlar, kargolar, iade taleplerini işler (para iadesi onayı hariç).',
    warehouse: 'Stok hareketleri, sayım ve transferleri yapar; fiyat ve müşteri iletişim bilgisini göremez.',
    finance: 'Ödemeler, iadeler, mutabakat, faturalar ve finans raporları.',
    marketing: 'Kampanya, kupon, e-posta/SMS ve reklam raporları.',
    content_editor: 'Sayfa ve makale taslakları yazar; yayınlayamaz.',
    vet_reviewer:
      'Sağlık/takviye içeriğini ve ürün sağlık beyanlarını onaylar. Bu onay olmadan sağlık içeriği yayınlanamaz.',
    customer_service: 'Müşteri ve sipariş bilgilerini görür, destek taleplerini yanıtlar.',
    dealer_rep: 'Kendine atanmış bayilerin sipariş ve tekliflerini yönetir.',
    read_only: 'Her şeyi görür, hiçbir şeyi değiştiremez; kişisel veriler maskelidir.',
  },
  permissions: {
    ...aiTr.permissions,
    ...faz8PermissionsTr,
    'org.settings.read': 'İşletme ayarlarını görüntüleme',
    'org.settings.update': 'İşletme bilgilerini ve ayarlarını değiştirme',
    'org.brand.read': 'Markaları görüntüleme',
    'org.brand.manage': 'Marka ekleme ve düzenleme',
    'org.store.read': 'Mağazaları görüntüleme',
    'org.store.manage': 'Mağaza, alan adı ve pazar yönetimi',
    'org.store.go_live': 'Mağazayı yayına alma, bakıma alma, yayından kaldırma (hassas)',
    'org.data.migrate': 'Taşıma paketini dışa/içe aktarma (yalnızca işletme sahibi; hassas)',
    'org.user.read': 'Kullanıcıları görüntüleme',
    'org.user.manage': 'Kullanıcı davet etme, rol değiştirme, askıya alma',
    'org.role.manage': 'Özel rol oluşturma ve düzenleme',
    'org.api_key.manage': 'API anahtarı oluşturma ve iptal etme',
    'org.webhook.manage': 'Webhook (olay bildirimi) yönetimi',
    'org.integration.read': 'Entegrasyon durumlarını görüntüleme',
    'org.integration.manage': 'Entegrasyon bilgilerini girme ve test etme',
    'org.integration.go_live': 'Entegrasyonu canlı ortama alma',
    'org.audit.read': 'Denetim kayıtlarını görüntüleme',
    'org.system.read': 'Sistem durumunu görüntüleme',
    'org.setup.manage': 'Kurulum sihirbazını yönetme',
    'org.help.manage': 'Yardım metinlerini düzenleme',
    'org.file.upload': 'Dosya ve görsel yükleme',
    'org.job.read': 'Arka plan işlerini görüntüleme',
    'catalog.product.read': 'Ürünleri görüntüleme',
    'catalog.product.write': 'Ürün ekleme ve düzenleme',
    'catalog.product.publish': 'Ürün yayınlama',
    'catalog.health_claim.approve': 'Ürün sağlık beyanını onaylama',
    'catalog.import': 'Ürün içe aktarma',
    'catalog.bulk.apply': 'Toplu ürün işlemi uygulama ve geri alma',
    'pricing.price.read': 'Fiyatları görüntüleme',
    'pricing.price.write': 'Fiyat düzenleme',
    'pricing.bulk.apply': 'Toplu fiyat değişikliği uygulama',
    'pricing.cost.read': 'Maliyet ve kâr marjını görüntüleme',
    'inventory.stock.read': 'Stokları görüntüleme',
    'inventory.stock.write': 'Stok hareketi ve sayım',
    'customers.customer.read': 'Müşterileri görüntüleme',
    'customers.customer.write': 'Müşteri düzenleme',
    'customers.pii.read': 'Müşteri kişisel verilerini maskesiz görme',
    'privacy.request.read': 'KVKK başvurularını görüntüleme',
    'privacy.request.manage': 'KVKK başvurusu kaydetme ve sonuçlandırma',
    'privacy.retention.read': 'Saklama sürelerini ve imha kaydını görme',
    'privacy.retention.manage': 'Otomatik imha kurallarını açma ve değiştirme',
    'privacy.data.export': 'KVKK: kişinin verilerinin kopyasını indirme',
    'privacy.data.erase': 'KVKK: kişisel verileri anonimleştirme (geri alınamaz)',
    'b2b.dealer.read': 'Bayileri görüntüleme',
    'b2b.dealer.manage': 'Bayi bilgilerini düzenleme',
    'b2b.dealer.approve': 'Bayi başvurusu onaylama/reddetme',
    'b2b.ledger.read': 'Bayi cari hesabını (borç/alacak, ekstre) görüntüleme',
    'b2b.ledger.manage': 'Bayi cari hesabına tahsilat, düzeltme ve açılış bakiyesi girme; vade belirleme',
    'b2b.quote.read': 'Bayi tekliflerini görüntüleme',
    'b2b.quote.manage': 'Bayi teklifi hazırlama, gönderme ve iptal etme',
    'b2b.sales.manage': 'Satış bölgelerini yönetme, bayiyi temsilciye ve bölgeye atama',
    'b2b.commission.read': 'Satış temsilcisi komisyonlarını görüntüleme',
    'b2b.commission.manage': 'Komisyon kuralı, dönem hesabı, onay ve ödendi işaretleme',
    'orders.order.read': 'Siparişleri görüntüleme',
    'orders.order.write': 'Sipariş düzenleme',
    'orders.refund.approve': 'Para iadesi onaylama',
    'payments.payment.read': 'Ödemeleri görüntüleme',
    'payments.payment.capture': 'Havale, kapıda ödeme ve açık hesap tahsilatını işaretleme',
    'payments.giftcard.read': 'Hediye kartlarını görüntüleme',
    'payments.giftcard.issue': 'Hediye kartı oluşturma, bakiye düzeltme, kapatma (hassas)',
    'customers.credit.manage': 'Müşteriye mağaza kredisi ekleme veya düşme (hassas)',
    'payments.provider.manage':
      'Ödeme ayarları, banka hesapları ve sağlayıcı anahtarları; kartla ödemeyi canlıya alma',
    'shipping.shipment.write': 'Kargo işlemleri',
    'shipping.method.manage': 'Kargo yöntemleri ve ücretleri',
    'invoices.invoice.read': 'Faturaları görüntüleme',
    'invoices.invoice.write': 'Fatura oluşturma ve gönderme',
    'legal.document.read': 'Yasal metinleri görüntüleme',
    'legal.document.write': 'Yasal metin taslağı yazma',
    'legal.document.publish': 'Yasal metin yayımlama',
    'content.read': 'İçerikleri görüntüleme',
    'content.write': 'İçerik yazma',
    'content.publish': 'İçerik yayınlama',
    'content.expert_review': 'Uzman (veteriner) onayı verme',
    'catalog.question.read': 'Ürün sorularını görüntüleme',
    'catalog.question.answer': 'Ürün sorularını yanıtlama ve yayımlama',
    'catalog.review.read': 'Ürün değerlendirmelerini görme',
    'catalog.review.moderate': 'Ürün değerlendirmelerini onaylama, reddetme ve yanıtlama',
    'theme.read': 'Temayı görüntüleme',
    'theme.edit': 'Tema düzenleme',
    'theme.publish': 'Temayı canlıya alma',
    'seo.read': 'SEO raporlarını görüntüleme',
    'seo.write': 'SEO ayarlarını düzenleme',
    'tracking.read': 'Ölçümleme ayarlarını ve çerez onayı istatistiklerini görüntüleme',
    'tracking.manage': 'Vitrin etiketlerini (GTM, GA4, Meta, TikTok) düzenleme',
    'marketing.campaign.read': 'Kampanyaları görüntüleme',
    'marketing.audience.read': 'Aboneleri, engel listesini ve İYS kayıtlarını görüntüleme',
    'marketing.audience.manage': 'Abonelik ve engel listesini yönetme, İYS kayıtlarını işaretleme',
    'marketing.segment.manage': "Müşteri segmentlerini oluşturma ve RFM'yi yeniden hesaplama",
    'marketing.campaign.write': 'Kampanya hazırlama',
    'marketing.campaign.send': 'Kampanya gönderme',
    'reports.sales.read': 'Satış raporları',
    'reports.finance.read': 'Finans raporları',
    'automation.manage': 'Otomasyon yönetimi',
    'marketing.loyalty.manage': 'Sadakat programı yönetimi',
    'marketing.referral.manage': 'Tavsiye programı yönetimi',
    'marketing.affiliate.manage': 'İş ortakları ve komisyon yönetimi',
    'customers.ticket.read': 'Destek taleplerini görüntüleme',
    'customers.ticket.manage': 'Destek taleplerini yanıtlama ve yönetme',
    'customers.chat.read': 'Canlı sohbet kuyruğunu görüntüleme',
    'customers.chat.manage': 'Canlı sohbette yanıtlama ve sohbet ayarları',
    'marketing.ads.read': 'Reklam merkezini görüntüleme',
    'marketing.ads.manage': 'Reklam hesapları, veri yükleme ve değişiklik isteği',
    'marketing.ads.approve': 'Reklam bütçe ve yayın değişikliklerini onaylama',
  },
  permissionModules: {
    ...aiTr.permissionModules,
    org: 'İşletme ve ayarlar',
    catalog: 'Katalog',
    pricing: 'Fiyat',
    inventory: 'Stok',
    customers: 'Müşteriler',
    b2b: 'Bayiler',
    orders: 'Siparişler',
    payments: 'Ödemeler',
    shipping: 'Kargo',
    invoices: 'Faturalar',
    content: 'İçerik',
    theme: 'Tema',
    seo: 'SEO',
    marketing: 'Pazarlama',
    reports: 'Raporlar',
    automation: 'Otomasyon',
  },
  integrationStatus: {
    NOT_CONFIGURED: 'Kurulum Gerekli',
    SANDBOX: 'Test (Sandbox)',
    LIVE: 'Canlı',
    ERROR: 'Hata',
    DISABLED: 'Kapalı',
  },
  integrationCategories: {
    email: 'E-posta',
    payment: 'Ödeme',
    google: 'Google',
    ads: 'Reklam ve ölçümleme',
    messaging: 'Mesajlaşma ve izinler',
    logistics: 'Kargo',
    invoicing: 'e-Fatura / e-Arşiv',
    infrastructure: 'Altyapı',
    ai: 'Yapay zekâ',
    mobile: 'Mobil',
    seo: 'SEO verisi',
  },
  integrations: {
    smtp: {
      name: 'SMTP e-posta',
      description: 'Sipariş, şifre ve davet e-postalarını kendi e-posta sunucunuzdan gönderir.',
    },
    ses: {
      name: 'Amazon SES',
      description:
        'E-postaları Amazon SES API ile gönderir; geri dönen ve şikâyet bildirimleri SNS ile engel listesine işlenir.',
    },
    sendgrid: {
      name: 'SendGrid',
      description:
        'E-postaları SendGrid API ile gönderir; imzalı olay bildirimleriyle geri dönen, şikâyet ve çıkmalar engel listesine işlenir.',
    },
    postmark: {
      name: 'Postmark',
      description:
        'İşlem ve bülten e-postalarını ayrı Postmark akışlarından gönderir; geri dönen, şikâyet ve çıkmalar engel listesine işlenir.',
    },
    iyzico: { name: 'iyzico', description: 'Kredi kartı, 3D Secure, taksit ve iade.' },
    paytr: { name: 'PayTR', description: 'Kredi kartı, taksit, havale/EFT ödeme sayfası.' },
    param: {
      name: 'Param',
      description:
        "Kurulum Gerekli: Param'ın barındırılan ödeme yönteminde tutar biçimi, dönüş alanları ve canlı adres herkese açık belgede yok; bağdaştırıcı için Param'dan belge istenmeli.",
    },
    sipay: {
      name: 'Sipay',
      description:
        "Kredi kartı, 3D Secure ve taksit Sipay ödeme sayfasında; kısmi iade. Canlı için Sipay'de sabit IP izni gerekir.",
    },
    paynet: {
      name: 'Paynet',
      description:
        "Paynet iyzico ile birleşti (1 Ocak 2026'dan beri iyzico markası); iyzico bağlantısını kullanın.",
    },
    stripe: {
      name: 'Stripe',
      description:
        'Döviz kart ödemeleri ve cüzdanlar (Apple Pay, Google Pay) Stripe ödeme sayfasında. Hesap, Stripe’ın desteklediği bir ülkedeki şirket adına açılır (Türkiye desteklenmiyor).',
    },
    paypal: {
      name: 'PayPal',
      description:
        'PayPal ile ödeme (EUR, USD, GBP). Hesap Türkiye dışındaki bir şirket adına açılır; PayPal Türkiye’de hizmet vermiyor.',
    },
    rank_tracking: {
      name: 'Sıralama takibi',
      description:
        'Anahtar kelimelerin Google sıralamasını günlük izlemek için lisanslı (ücretli) bir veri sağlayıcı gerekir (ör. DataForSEO, Semrush). Sağlayıcı seçimi sahipte; o zamana kadar konum bilgisi Search Console ortalamasından gelir.',
    },
    pagespeed: {
      name: 'PageSpeed Insights',
      description:
        'Sayfa hızı ölçümü (Core Web Vitals): ana sayfa, kategori, ürün ve blog yazısı haftalık ölçülür. Google Cloud API anahtarı; ücretsiz.',
    },
    google_oauth: {
      name: 'Google hesabı (OAuth istemcisi)',
      description:
        'Search Console, Analytics ve Merchant Center bağlantıları için Google Cloud OAuth istemcisi. Bağlantı Google ekranından yapılır.',
    },
    google_merchant: {
      name: 'Google Merchant Center',
      description: 'Ürün feed’i, ücretsiz listelemeler ve Shopping.',
    },
    google_search_console: {
      name: 'Google Search Console',
      description: 'Arama performansı, sitemap ve indeksleme raporları.',
    },
    google_analytics: { name: 'Google Analytics 4', description: 'Ziyaret ve satış analitiği.' },
    google_tag_manager: { name: 'Google Tag Manager', description: 'Etiket yönetimi.' },
    google_ads: { name: 'Google Ads', description: 'Reklam raporları ve dönüşüm yükleme.' },
    meta_ads: {
      name: 'Meta reklam harcaması',
      description:
        'Facebook ve Instagram reklam harcaması, gösterim ve tıklama verisini günlük okur (yalnızca okuma).',
    },
    tiktok_ads: {
      name: 'TikTok reklam harcaması',
      description: 'TikTok reklam harcaması, gösterim ve tıklama verisini günlük okur (yalnızca okuma).',
    },
    google_business_profile: { name: 'Google Business Profile', description: 'İşletme bilgisi ve yorumlar.' },
    meta: { name: 'Meta (Facebook/Instagram)', description: 'Piksel ve Conversions API.' },
    tiktok: { name: 'TikTok', description: 'Piksel ve Events API.' },
    iys: {
      name: 'İYS (İleti Yönetim Sistemi)',
      description:
        "Ticari ileti izin ve retlerinin üç iş günü içinde İYS'ye bildirilmesi. Bağlı değilken kayıtlar Pazarlama → İYS kayıtları ekranından dosyayla bildirilir.",
    },
    netgsm: {
      name: 'Netgsm (SMS)',
      description:
        'Kampanya SMS’leri ve teslim raporu. Başlık (gönderici adı) Netgsm’de onaylı, hesapta İYS marka kodu tanımlı olmalı. Bağlantı testi bakiye sorgular; SMS gönderilmez.',
    },
    iletimerkezi: {
      name: 'İleti Merkezi (SMS)',
      description:
        'Kampanya SMS’leri ve teslim raporu. Başlık İleti Merkezi’nde onaylı olmalı. Bağlantı testi bakiye sorgular; SMS gönderilmez.',
    },
    verimor: {
      name: 'Verimor (SMS)',
      description:
        'Kampanya SMS’leri ve teslim raporu. Verimor’da API IP izni ve onaylı başlık gerekir. Bağlantı testi bakiye sorgular; SMS gönderilmez.',
    },
    whatsapp: {
      name: 'WhatsApp Business (Cloud API)',
      description:
        'Meta’da onaylı şablonla kampanya iletisi ve teslim bildirimi (webhook). Bağlantı testi telefon numarası bilgisini okur; ileti gönderilmez.',
    },
    yurtici: { name: 'Yurtiçi Kargo', description: 'Gönderi kaydı ve takip (firma web servisi).' },
    aras: { name: 'Aras Kargo', description: 'Gönderi kaydı ve takip (firma web servisi).' },
    mng: { name: 'MNG Kargo (DHL eCommerce)', description: 'Gönderi, barkod etiketi ve takip (Apizone).' },
    geliver: {
      name: 'Geliver (çoklu kargo)',
      description: 'Tek bağlantıyla birçok kargo firması: teklif, etiket, takip.',
    },
    google_signin: {
      name: 'Google ile giriş (müşteriler)',
      description:
        'Vitrin müşterileri için "Google ile devam et": ayrı OAuth istemcisi; kapsam yalnızca openid, e-posta, profil. Mağaza bazında Mağazalar → Müşteri girişi bölümünden açılır.',
    },
    bizimhesap: {
      name: 'BizimHesap',
      description: 'Siparişten fatura kaydı; e-Arşiv/e-Fatura BizimHesap panelinden.',
    },
    cloudflare: {
      name: 'Cloudflare',
      description:
        'CDN, WAF ve DNS. Kullanma kararı sahipte (zorunlu değil); site Cloudflare olmadan çalışır.',
    },
    s3_storage: {
      name: 'Nesne depolama (S3 / R2)',
      description:
        'Cloudflare R2, Backblaze B2, Hetzner veya başka bir S3 uyumlu depo: görsel ve belgelerin sunucu dışında saklanması ve gece yedeklerinin şifreli uzak kopyası.',
    },
    error_tracking: { name: 'Hata takibi', description: 'Uygulama hatalarının izlenmesi.' },
    observability: {
      name: 'Gözlemlenebilirlik (OpenTelemetry)',
      description:
        'İstek ve arka plan işi süreleri, hata oranları ve izler OTLP ile bir toplayıcıya (Grafana Cloud, Honeycomb, New Relic, kendi OpenTelemetry Collector) gönderilir. Kişisel veri gönderilmez; yalnızca yöntem, rota şablonu, durum kodu ve iş adı.',
    },
    firebase: {
      name: 'Firebase (Android bildirimleri)',
      description:
        'Android yönetim uygulamasına yeni sipariş, iade talebi gibi bildirimler (FCM). Firebase projesindeki hizmet hesabı anahtarının project_id, client_email ve private_key alanları girilir.',
    },
    // Faz 9: ai_text, ai_image ve youtube metinleri ai.*.ts'de.
    ...aiTr.integrations,
  },
  integrationFields: {
    ...aiTr.integrationFields,
    iysCode: 'İYS numarası (hizmet sağlayıcı kodu)',
    brandCode: 'Marka kodu',
    autoSend: "İzin ve retleri İYS'ye otomatik gönder",
    endpoint: 'Uç nokta adresi (S3 API, https://…)',
    region: 'Bölge (Amazon için ör. eu-central-1; R2 için auto, boşsa auto)',
    bucket: 'Kova (bucket) adı',
    accessKeyId: 'Erişim anahtarı kimliği (Access Key ID)',
    secretAccessKey: 'Gizli erişim anahtarı (Secret Access Key)',
    prefix: 'Klasör öneki (isteğe bağlı, ör. zootopia/)',
    storeMedia: 'Yeni görselleri ve belgeleri bu depoda sakla',
    backupCopies: 'Yedeklerin şifreli uzak kopyasını bu depoya yükle',
    backupKeepDays: 'Uzak yedek saklama süresi (gün; boşsa 30)',
    dsn: 'DSN (hata takibi proje adresi)',
    otlpEndpoint: 'Toplayıcı adresi (OTLP/HTTP, https://…)',
    otlpHeaders: 'Kimlik başlıkları (Ad=değer, virgülle; ör. Authorization=Basic …)',
    sampleRatio: 'İz örnekleme oranı (0–1; boş = 1, tüm istekler)',
    host: 'Sunucu adresi',
    port: 'Port',
    secure: 'SSL/TLS (465 portu için açın)',
    username: 'Kullanıcı adı',
    password: 'Şifre',
    fromEmail: 'Gönderen e-posta',
    fromName: 'Gönderen adı',
    apiKey: 'API anahtarı',
    secretKey: 'Gizli anahtar',
    merchantId: 'Mağaza no',
    merchantKey: 'Mağaza anahtarı',
    merchantSalt: 'Mağaza gizli tuzu',
    wsUserName: 'Web servis kullanıcı adı',
    wsPassword: 'Web servis şifresi',
    codUserName: 'Kapıda ödeme kullanıcı adı (varsa)',
    codPassword: 'Kapıda ödeme şifresi (varsa)',
    setOrderUserName: 'Gönderi oluşturma kullanıcı adı',
    setOrderPassword: 'Gönderi oluşturma şifresi',
    queryUserName: 'Sorgulama kullanıcı adı',
    queryPassword: 'Sorgulama şifresi',
    customerCode: 'Müşteri kodu',
    clientId: 'İstemci kimliği (Client ID)',
    clientSecret: 'İstemci gizli anahtarı (Client Secret)',
    webhookSecret: 'Bildirim imza anahtarı (whsec_…)',
    appId: 'Uygulama kimliği (app_id)',
    saleWebhookKey: 'Satış bildirimi anahtarı',
    webhookId: 'Bildirim (webhook) kimliği',
    customerNumber: 'Müşteri numarası',
    apiToken: 'API anahtarı (token)',
    firmId: 'Firma kimliği (firmId)',
    token: 'Erişim anahtarı (token; boşsa firma kimliği kullanılır)',
    configurationSetName: 'Yapılandırma kümesi (Configuration set; isteğe bağlı)',
    snsTopicArn: 'Bildirim konusu (SNS Topic ARN; geri dönen/şikâyet bildirimleri için)',
    webhookPublicKey: 'Olay bildirimi doğrulama anahtarı (Signed Event Webhook public key)',
    euRegion: 'AB bölgesi hesabı (api.eu.sendgrid.com)',
    serverToken: 'Sunucu anahtarı (Server API token)',
    transactionalStream: 'İşlem e-postası akışı (boşsa outbound)',
    broadcastStream: 'Bülten/kampanya akışı (Broadcast stream kimliği; boşsa broadcast)',
    apiHash: 'API gizli anahtarı (hash)',
    header: 'SMS başlığı (onaylı gönderici adı, en çok 11 karakter)',
    accessToken: 'Erişim anahtarı (sistem kullanıcısı, kalıcı)',
    phoneNumberId: 'Telefon numarası kimliği (Phone number ID)',
    wabaId: 'WhatsApp işletme hesabı kimliği (WABA ID)',
    appSecret: 'Uygulama gizli anahtarı (app_secret / App secret)',
    verifyToken: 'Webhook doğrulama anahtarı (Verify token; sizin belirlediğiniz)',
    apiVersion: 'Graph API sürümü (isteğe bağlı, boşsa v23.0)',
    projectId: 'Proje kimliği (project_id)',
    clientEmail: 'Hizmet hesabı e-postası (client_email)',
    privateKey: 'Özel anahtar (JSON dosyasındaki private_key değerinin tamamı)',
    loginCustomerId: 'Yönetici hesap kimliği (MCC, isteğe bağlı, 123-456-7890)',
    adAccountId: 'Reklam hesabı kimliği (act_ ile ya da yalnızca rakamlarla)',
    advertiserId: 'Reklamveren kimliği (Advertiser ID)',
  },
  integrationMessages: {
    ...aiTr.integrationMessages,
    health_timeout: 'Sağlayıcı zamanında yanıt vermedi.',
    health_unreachable: 'Sağlayıcıya ulaşılamadı.',
    health_skipped_messages:
      'Bu test sağlayıcıya gerçek bir ileti veya olay gönderir; otomatik denenmez. Bağlantının "Bağlantıyı test et" düğmesini kullanın.',
    health_skipped_remote_write:
      'Bu test sağlayıcıda deneme kaydı oluşturup siler; otomatik denenmez. Bağlantının "Bağlantıyı test et" düğmesini kullanın.',
    health_skipped_paid_quota:
      'Bu test ücretli kota harcar; otomatik denenmez. Bağlantının "Bağlantıyı test et" düğmesini kullanın.',
    health_skipped_unknown: 'Bu bağlantı için otomatik denetim tanımlı değil.',
    ses_ok: "Amazon SES'e bağlanıldı; gönderim açık.",
    ses_ok_sandbox:
      'Amazon SES\'e bağlanıldı ancak hesap deneme ortamında (sandbox): yalnızca doğrulanmış adreslere gönderir. AWS\'den "production access" isteyin.',
    ses_invalid_settings: 'Bölge (ör. eu-central-1), erişim anahtarları ve gönderen e-posta gerekli.',
    ses_auth_failed: 'Amazon erişim anahtarları kabul edilmedi veya SES izni yok.',
    ses_unreachable: "Amazon SES'e ulaşılamadı.",
    ses_sending_disabled: 'Bu Amazon SES hesabında gönderim kapalı (askıya alınmış olabilir).',
    sendgrid_ok: "SendGrid'e bağlanıldı; anahtarın e-posta gönderme izni var.",
    sendgrid_invalid_settings: 'API anahtarı ve gönderen e-posta gerekli.',
    sendgrid_auth_failed: 'SendGrid API anahtarı kabul edilmedi.',
    sendgrid_missing_scope: 'API anahtarının "Mail Send" izni yok.',
    sendgrid_unreachable: "SendGrid'e ulaşılamadı.",
    postmark_ok: "Postmark'a bağlanıldı.",
    postmark_invalid_settings: 'Sunucu anahtarı ve gönderen e-posta gerekli.',
    postmark_auth_failed: 'Postmark sunucu anahtarı kabul edilmedi.',
    postmark_unreachable: "Postmark'a ulaşılamadı.",
    iys_ok: "İYS'ye bağlanıldı (jeton alındı); izin gönderilmedi.",
    iys_invalid_settings: 'İYS numarası, marka kodu (yalnızca rakam), kullanıcı adı ve şifre gerekli.',
    iys_auth_failed: 'İYS kullanıcı adı veya şifresi kabul edilmedi.',
    iys_rejected: 'İYS kaydı reddetti.',
    iys_unreachable: "İYS'ye ulaşılamadı.",
    s3_ok: 'Depoya deneme dosyası yazıldı, okundu ve silindi; bağlantı çalışıyor.',
    s3_endpoint_invalid:
      'Uç nokta, kova adı veya anahtarlar eksik ya da hatalı (https ile başlayan S3 API adresi gerekir).',
    s3_auth_failed: 'Depo anahtarları kabul edilmedi veya bu kovaya yazma izni yok.',
    s3_bucket_missing: 'Bu adla bir kova yok; sağlayıcının panelinde kovayı oluşturun.',
    s3_unreachable: 'Depo sunucusuna ulaşılamadı.',
    s3_verify_failed: 'Yazılan deneme dosyası aynı okunamadı.',
    error_tracking_ok:
      'Hata takibi servisine deneme olayı gönderildi; servisin panelinde "Zootopia bağlantı testi" görünmeli.',
    error_tracking_invalid_dsn:
      'DSN biçimi hatalı. Servisin proje ayarlarındaki "Client Keys (DSN)" adresini aynen yapıştırın (https://…@…/proje-no).',
    error_tracking_failed:
      'Hata takibi servisi deneme olayını kabul etmedi; DSN doğru ve proje etkin mi kontrol edin.',
    observability_ok:
      'Toplayıcı boş bir iz paketini kabul etti. Bundan sonra istek ve iş süreleri birkaç dakika içinde toplayıcının panelinde görünür.',
    observability_invalid:
      'Toplayıcı adresi veya başlıklar hatalı. Adres https ile başlamalı (ör. https://otlp-gateway-….grafana.net/otlp); başlıklar "Ad=değer" biçiminde, virgülle ayrılmış.',
    observability_failed:
      'Toplayıcı deneme paketini kabul etmedi; adres ve kimlik başlığını (ör. Authorization) toplayıcının OTLP ayarlarından kontrol edin.',
    pagespeed_ok: 'PageSpeed Insights anahtarı doğrulandı; örnek sayfa ölçüldü.',
    pagespeed_key_format: 'API anahtarı biçimi hatalı.',
    pagespeed_key_invalid: 'Google bu API anahtarını kabul etmedi.',
    pagespeed_quota: 'PageSpeed Insights günlük kotası doldu.',
    pagespeed_unreachable: 'PageSpeed Insights’a ulaşılamadı.',
    smtp_ok: 'SMTP sunucusuna bağlanıldı ve kimlik doğrulandı.',
    google_client_ok:
      'Google istemci kimliği ve gizli anahtarı doğrulandı. Şimdi Google ekranından hesabınızı bağlayın.',
    google_client_invalid: 'Google istemci kimliği veya gizli anahtarı kabul edilmedi.',
    google_client_invalid_format:
      'İstemci kimliği "….apps.googleusercontent.com" ile bitmeli; gizli anahtar boş olamaz.',
    google_signin_needs_domain:
      "Kurulum Gerekli: test, mağazanın gerçek dönüş adresiyle yapılır. Önce Mağazalar'dan bir vitrin alan adı ekleyin.",
    google_invalid_request:
      "Google isteği reddetti (invalid_request): {{description}} Denenen dönüş adresi: {{redirectUri}}. Adres gerçek bir alan adı ve https olmalı; Google Cloud'da istemcinin yetkili yönlendirme URI'leri arasında bulunmalı.",
    google_redirect_uri_mismatch:
      "Dönüş adresi Google Cloud'daki istemcide kayıtlı değil (redirect_uri_mismatch): {{redirectUri}}. Google Auth Platform → İstemciler → istemci → Yetkili yönlendirme URI'leri'ne ekleyin. {{description}}",
    google_client_rejected: 'Google isteği reddetti ({{error}}): {{description}}',
    google_unreachable: "Google'a ulaşılamadı. Daha sonra tekrar deneyin.",
    smtp_failed: 'SMTP bağlantısı başarısız.',
    smtp_invalid_settings: 'Sunucu adresi veya port hatalı.',
    smtp_private_host_blocked: 'Güvenlik nedeniyle iç ağ adreslerine bağlanılamaz.',
    iyzico_ok: 'iyzico bağlantısı doğrulandı.',
    iyzico_credentials_missing: 'iyzico API anahtarı ve gizli anahtar girilmemiş.',
    iyzico_not_sandbox_key: 'Test ortamı için iyzico "sandbox-" ile başlayan test anahtarı gerekir.',
    iyzico_sandbox_key_in_live: 'Canlı ortama test (sandbox) anahtarı girilmiş.',
    iyzico_auth_failed: 'iyzico anahtarları kabul edilmedi.',
    iyzico_unreachable: "iyzico'ya ulaşılamadı. Daha sonra tekrar deneyin.",
    paytr_ok: 'PayTR bağlantısı doğrulandı.',
    paytr_credentials_missing: 'PayTR mağaza no, mağaza anahtarı ve gizli tuz girilmemiş.',
    paytr_auth_failed: 'PayTR bilgileri kabul edilmedi.',
    paytr_unreachable: "PayTR'ye ulaşılamadı. Daha sonra tekrar deneyin.",
    stripe_ok: 'Stripe bağlantısı doğrulandı (bakiye okundu).',
    stripe_credentials_missing: 'Stripe gizli anahtarı ve bildirim imza anahtarı girilmemiş.',
    stripe_key_invalid:
      'Gizli anahtar "sk_" veya kısıtlı anahtar "rk_" ile başlamalı (yayınlanabilir "pk_" anahtarı değil).',
    stripe_not_test_key: 'Test ortamı için "sk_test_" ile başlayan test anahtarı gerekir.',
    stripe_test_key_in_live: 'Canlı ortama test anahtarı girilmiş ("sk_live_" gerekir).',
    stripe_webhook_secret_invalid:
      'Bildirim imza anahtarı "whsec_" ile başlamalı (Stripe → Webhooks → uç nokta).',
    stripe_mode_mismatch: 'Anahtarın ortamı (test/canlı) seçilen ortamla uyuşmuyor.',
    stripe_auth_failed: 'Stripe anahtarı kabul edilmedi.',
    stripe_unreachable: "Stripe'a ulaşılamadı. Daha sonra tekrar deneyin.",
    sipay_ok: 'Sipay bağlantısı doğrulandı (erişim anahtarı alındı).',
    sipay_credentials_missing:
      'Sipay uygulama kimliği, uygulama gizli anahtarı ve mağaza anahtarı girilmemiş.',
    sipay_auth_failed: 'Sipay bilgileri kabul edilmedi.',
    sipay_unreachable: "Sipay'e ulaşılamadı. Daha sonra tekrar deneyin.",
    google_signin_client_ok: 'İstemci kimliği ve gizli anahtar Google tarafından kabul edildi.',
    geliver_ok:
      'Geliver bağlantısı doğrulandı; {{senders}} gönderici adresi bulundu. Canlı ortam: etiket ücreti Geliver bakiyesinden düşer.',
    geliver_ok_test:
      "Geliver bağlantısı doğrulandı; {{senders}} gönderici adresi bulundu. Test ortamı: gönderiler Geliver'e test gönderisi olarak açılır.",
    geliver_credentials_missing: 'Geliver API anahtarı girilmemiş.',
    geliver_auth_failed:
      'Geliver API anahtarı kabul edilmedi. Anahtarı Geliver panelinde (API anahtarları) kontrol edin.',
    geliver_timeout: 'Geliver 30 saniye içinde yanıt vermedi. Daha sonra tekrar deneyin.',
    geliver_unreachable: "Geliver'e ulaşılamadı. Daha sonra tekrar deneyin.",
    geliver_unavailable:
      'Geliver şu an yanıt veremiyor (HTTP {{status}}). Birkaç dakika sonra tekrar deneyin; etiket iki kez satın alınmaz.',
    geliver_rejected: 'Geliver isteği reddetti: {{reason}}',
    geliver_unexpected_response: 'Geliver beklenmeyen bir yanıt verdi; işlem tamamlanmadı.',
    paypal_ok: 'PayPal bağlantısı doğrulandı (bildirim kaydı bulundu).',
    paypal_credentials_missing: 'PayPal istemci kimliği, gizli anahtar ve bildirim kimliği girilmemiş.',
    paypal_auth_failed: 'PayPal istemci kimliği veya gizli anahtarı kabul edilmedi.',
    paypal_webhook_not_found: 'Bu bildirim (webhook) kimliği PayPal uygulamanızda bulunamadı.',
    paypal_unreachable: "PayPal'a ulaşılamadı. Daha sonra tekrar deneyin.",
    bizimhesap_ok: 'BizimHesap bağlantısı doğrulandı.',
    bizimhesap_credentials_missing: 'BizimHesap firma kimliği (firmId) girilmemiş.',
    bizimhesap_auth_failed: 'BizimHesap bilgileri kabul edilmedi. Firma kimliğini/anahtarı kontrol edin.',
    bizimhesap_unreachable: "BizimHesap'a ulaşılamadı. Daha sonra tekrar deneyin.",
    bizimhesap_redirected:
      'BizimHesap isteği başka bir adrese yönlendirdi (HTTP {{status}} → {{location}}). BizimHesap API adresi değişmiş olabilir; platformun güncellenmesi gerekir.',
    bizimhesap_unexpected_response:
      'BizimHesap beklenmeyen bir yanıt verdi (HTTP {{status}}, {{contentType}}); yanıt JSON değil. Hizmet bakımda veya API adresi değişmiş olabilir.',
    bizimhesap_timeout: 'BizimHesap 20 saniye içinde yanıt vermedi. Daha sonra tekrar deneyin.',
    netgsm_ok: 'Netgsm bağlantısı doğrulandı (bakiye okundu); SMS gönderilmedi.',
    netgsm_invalid_settings: 'Kullanıcı bilgileri ve en çok 11 karakterlik SMS başlığı gerekli.',
    netgsm_auth_failed: 'Netgsm kullanıcı bilgilerini kabul etmedi.',
    netgsm_unreachable: "Netgsm'e ulaşılamadı. Daha sonra tekrar deneyin.",
    netgsm_rejected: 'Netgsm isteği reddetti; hesap ayarlarını kontrol edin.',
    iletimerkezi_ok: 'İleti Merkezi bağlantısı doğrulandı (bakiye okundu); SMS gönderilmedi.',
    iletimerkezi_invalid_settings: 'Kullanıcı bilgileri ve en çok 11 karakterlik SMS başlığı gerekli.',
    iletimerkezi_auth_failed: 'İleti Merkezi kullanıcı bilgilerini kabul etmedi.',
    iletimerkezi_unreachable: "İleti Merkezi'ne ulaşılamadı. Daha sonra tekrar deneyin.",
    iletimerkezi_rejected: 'İleti Merkezi isteği reddetti; hesap ayarlarını kontrol edin.',
    verimor_ok: 'Verimor bağlantısı doğrulandı (bakiye okundu); SMS gönderilmedi.',
    verimor_invalid_settings: 'Kullanıcı bilgileri ve en çok 11 karakterlik SMS başlığı gerekli.',
    verimor_auth_failed: 'Verimor kullanıcı bilgilerini kabul etmedi.',
    verimor_unreachable: "Verimor'a ulaşılamadı. IP izni tanımlı mı kontrol edin.",
    verimor_rejected: 'Verimor isteği reddetti; hesap ayarlarını kontrol edin.',
    whatsapp_ok: 'WhatsApp Business bağlantısı doğrulandı (numara bilgisi okundu); ileti gönderilmedi.',
    whatsapp_invalid_settings:
      'Erişim anahtarı, telefon numarası kimliği, WABA kimliği, uygulama gizli anahtarı ve doğrulama anahtarı gerekli.',
    whatsapp_auth_failed: 'Meta erişim anahtarını kabul etmedi veya izni yok.',
    whatsapp_unreachable: "Meta'ya (WhatsApp) ulaşılamadı.",
    whatsapp_rejected: 'Meta isteği reddetti; telefon numarası kimliğini kontrol edin.',
    firebase_ok: 'Firebase hizmet hesabı doğrulandı (erişim anahtarı alındı); bildirim gönderilmedi.',
    firebase_invalid_settings:
      'Proje kimliği, …iam.gserviceaccount.com ile biten hizmet hesabı e-postası ve özel anahtar (PEM) gerekli.',
    firebase_auth_failed:
      'Google hizmet hesabını kabul etmedi; anahtarın silinmediğini ve e-postanın doğru olduğunu kontrol edin.',
    firebase_unreachable: "Google'a ulaşılamadı. Daha sonra tekrar deneyin.",
    google_ads_invalid_settings: 'Yönetici hesap (MCC) kimliği 10 rakam olmalı (ör. 123-456-7890).',
    google_ads_settings_saved:
      'Kaydedildi. Geliştirici jetonu artık gerekmiyor (Google, 9 Eylül 2026); erişim Google Cloud projenizin düzeyine bağlı. Yönetici (MCC) kimliği yalnızca hesaba bir yönetici hesap üzerinden erişiyorsanız gerekir.',
    google_ads_cloud_project_not_approved:
      "Google Cloud projeniz Google Ads API ile yalnızca test hesaplarına erişebiliyor (Test erişimi). Gerçek reklam hesabı için Google Cloud'da Explorer erişimine başvurun: Google Cloud Console → Google Ads API → Genel bakış (Google bağlantısındaki OAuth istemcisinin projesi).",
    google_ads_user_permission_denied:
      "Bağlı Google hesabı bu Google Ads hesabına erişemiyor. Hesaba bir yönetici (MCC) hesabı üzerinden erişiyorsanız Entegrasyonlar → Google Ads'e MCC kimliğini girin; girdiyseniz bu hesabı yönettiğini kontrol edin.",
    google_ads_customer_not_enabled: 'Bu Google Ads hesabı henüz etkin değil ya da kapatılmış.',
    google_ads_authorization_error:
      "Google Ads yetkilendirmeyi reddetti. Cloud projesinin erişim düzeyi 9 Eylül 2026'dan sonra yükseltildiyse bu, Google'ın bildirdiği bilinen bir sorun olabilir: yeni bir Google Cloud projesi oluşturup Explorer erişimine başvurun.",
    google_ads_api_not_enabled:
      "Google Cloud projesinde Google Ads API etkin değil veya hizmet şartları kabul edilmemiş. Google Cloud Console'da Google Ads API'yi etkinleştirin.",
    google_scope_missing: 'Bu hizmet için Google izni verilmedi. Hizmeti seçerek yeniden bağlanın.',
    google_permission_denied:
      'Bağlı Google hesabının bu kaynağa erişimi yok. Kaynağın (mülk, site, hesap) bu Google hesabıyla paylaşıldığını kontrol edin ya da erişimi olan hesapla yeniden bağlanın.',
    google_api_not_enabled:
      "Bu Google hizmetinin API'si Google Cloud projesinde etkin değil. Google Cloud Console → API'ler ve Hizmetler → Kitaplık'tan ilgili API'yi etkinleştirin (Google bağlantısındaki OAuth istemcisinin projesi).",
    google_quota_exceeded:
      'Google istek sınırına ulaşıldı. Biraz sonra yeniden deneyin; eşitlemeler kendiliğinden yeniden denenir.',
    google_ads_quota_exceeded:
      'Google Ads API istek sınırına ulaşıldı. Explorer erişiminde gerçek hesapta günlük işlem sınırı vardır; işlem daha sonra kendiliğinden yeniden denenir.',
    google_ads_access_prohibited:
      "Google Ads API erişimi bu Google Cloud projesi için engellenmiş. Google Cloud Console'da Google Ads API erişim düzeyini kontrol edin.",
    google_ads_action_not_permitted:
      'Bağlı Google hesabının bu Google Ads hesabında bu işlemi yapma yetkisi yok (yalnızca okuma erişimi olabilir). Hesaba Standart veya Yönetici erişimiyle bağlanın.',
    google_ads_account_suspended:
      "Google Ads hesabı askıya alınmış; işlem yapılamaz. Google Ads'te hesabın durumuna bakın.",
    google_ads_not_allowlisted:
      'Bu Google Ads özelliği hesabınız için açık değil (Google izin listesi gerekiyor).',
    google_ads_policy_violation:
      'Google reklam politikası nedeniyle reddedildi. Metni veya ürünü düzenleyin; sağlık ürünlerinde "tedavi eder", "önler", "iyileştirir" gibi ifadeler kullanılmaz. Ayrıntı kampanyanın politika durumunda.',
    google_ads_budget_too_low: 'Günlük bütçe Google Ads için çok düşük. Daha yüksek bir günlük bütçe girin.',
    google_ads_budget_too_high: 'Günlük bütçe Google Ads üst sınırını aşıyor.',
    google_ads_budget_currency: 'Bütçe tutarı hesabın para birimine veya en küçük birimine uymuyor.',
    google_ads_budget_invalid:
      'Google Ads bütçe ayarını reddetti. Bütçenin başka kampanyada kullanılmadığını ve silinmediğini kontrol edin.',
    google_ads_duplicate_name: 'Bu adla bir kampanya veya bütçe zaten var. Farklı bir ad girin.',
    google_ads_bid_out_of_range: 'Teklif tutarı Google Ads sınırlarının dışında.',
    google_ads_bidding_invalid: 'Seçilen teklif stratejisi bu kampanya türünde veya bütçeyle kullanılamıyor.',
    google_ads_temporary_error:
      'Google Ads geçici bir hata verdi. İşlem kısa süre sonra kendiliğinden yeniden denenir.',
    google_ads_resource_not_found:
      "Kaynak Google Ads'te bulunamadı (Google Ads ekranından silinmiş olabilir). Eşitleyip yeniden deneyin.",
    google_ads_value_out_of_range: 'Girilen değer Google Ads sınırlarının dışında.',
    google_ads_resource_limit:
      'Google Ads hesabının kaynak sınırına ulaşıldı (ör. kampanya veya öğe sayısı).',
    google_ads_request_rejected: 'Google Ads isteği reddetti. Ayrıntı için Google bağlantı günlüğüne bakın.',
    ads_ok: 'Reklam hesabı okundu: {{account}} ({{currency}}). Harcama verisi her sabah eşitlenir.',
    ads_credentials_format:
      'Erişim anahtarı ve hesap kimliği gerekli; hesap kimliği yalnızca rakamlardan oluşmalı.',
    ads_token_invalid: 'Erişim anahtarı geçersiz veya süresi dolmuş. Yeni bir anahtar oluşturup kaydedin.',
    ads_permission: 'Anahtarın bu reklam hesabını okuma izni yok (ads_read / rapor izni).',
    ads_rate_limited: 'Platform istek sınırına ulaşıldı; eşitleme daha sonra yeniden denenir.',
    ads_account_not_found: 'Reklam hesabı bulunamadı; kimliği kontrol edin.',
    ads_unreachable: 'Reklam platformuna ulaşılamadı. Daha sonra tekrar deneyin.',
    ads_timeout: 'Reklam platformu zamanında yanıt vermedi. Daha sonra tekrar deneyin.',
    ads_bad_response: 'Reklam platformundan beklenmeyen yanıt geldi.',
    ads_provider_error: 'Reklam platformu isteği reddetti. Ayrıntı için platformun hata kaydına bakın.',
  },
  setupSteps: {
    business: 'İşletme bilgileri',
    brand: 'Marka ekle',
    logo: 'Logo yükle',
    colors: 'Renk seç',
    domain: 'Alan adı bağla',
    currency: 'Para birimi ve pazar',
    tax: 'Vergi ayarla',
    import_products: 'Ürünleri içe aktar',
    warehouse: 'Depo oluştur',
    shipping: 'Kargo bağla',
    pos_application: 'POS başvurusunu hazırla',
    pos_connect: 'POS bağla',
    email_service: 'E-posta servisini bağla',
    search_console: 'Search Console bağla',
    ga4: 'GA4 bağla',
    gtm: 'GTM bağla',
    merchant_center: 'Merchant Center bağla',
    seo: 'SEO ayarlarını tamamla',
    legal_pages: 'Hukuki sayfaları kontrol et',
    test_order: 'Test siparişi oluştur',
    payment_tests: 'Ödeme testlerini çalıştır',
    security_checks: 'Güvenlik kontrollerini tamamla',
    preview_store: 'Mağazayı önizle',
    go_live: 'Yetkili onayıyla canlıya al',
  },
  setupStatus: { done: 'Tamamlandı', pending: 'Bekliyor', skipped: 'Atlandı', locked: 'Kilitli' },
  storefront: storefrontTr,
  auditActions: {
    ...aiTr.auditActions,
    'company.member.add': 'Firma kullanıcısı eklendi',
    'company.member.update': 'Firma kullanıcısının rolü veya eşiği değişti',
    'company.member.remove': 'Firma kullanıcısı çıkarıldı',
    'customer.access_link.activation': 'Etkinleştirme bağlantısı oluşturuldu',
    'api_key.create': 'API anahtarı oluşturuldu',
    'api_key.revoke': 'API anahtarı iptal edildi',
    'brand.create': 'Marka eklendi',
    'brand.update': 'Marka güncellendi',
    'domain.create': 'Alan adı eklendi',
    'domain.delete': 'Alan adı kaldırıldı',
    'help.update': 'Yardım metni güncellendi',
    'integration.credentials_saved': 'Entegrasyon bilgileri kaydedildi',
    'integration.disconnected': 'Entegrasyon bağlantısı kesildi',
    'integration.tested': 'Entegrasyon test edildi',
    'market.create': 'Pazar eklendi',
    'market.delete': 'Pazar kaldırıldı',
    'member.invitation_accepted': 'Davet kabul edildi',
    'member.invitation_revoke': 'Davet geri alındı',
    'member.invite': 'Kullanıcı davet edildi',
    'member.remove': 'Kullanıcı çıkarıldı',
    'member.update': 'Kullanıcının rolü değişti',
    'organization.update': 'İşletme bilgileri güncellendi',
    'role.create': 'Özel rol oluşturuldu',
    'role.delete': 'Özel rol silindi',
    'role.update': 'Özel rol güncellendi',
    'settings.update': 'Ayarlar güncellendi',
    'setup.step_marked': 'Kurulum adımı işaretlendi',
    'store.create': 'Mağaza oluşturuldu',
    'store.update': 'Mağaza güncellendi',
    'webhook.create': 'Webhook eklendi',
    'webhook.delete': 'Webhook silindi',
    'webhook.redeliver': 'Webhook yeniden gönderildi',
    'webhook.rotate_secret': 'Webhook imza sırrı yenilendi',
    'webhook.update': 'Webhook güncellendi',
  },
  auditEntities: {
    company: 'Bayi firması',
    customer: 'Müşteri',
    api_key: 'API anahtarı',
    brand: 'Marka',
    custom_role: 'Özel rol',
    domain: 'Alan adı',
    help_entry: 'Yardım metni',
    integration: 'Entegrasyon',
    invitation: 'Davet',
    market: 'Pazar',
    membership: 'Kullanıcı',
    organization: 'İşletme',
    settings: 'Ayarlar',
    setup_step: 'Kurulum adımı',
    store: 'Mağaza',
    webhook_delivery: 'Webhook teslimi',
    webhook_endpoint: 'Webhook',
  },
  healthClaims: {
    treatment: 'Tedavi iddiası',
    cure: 'İyileştirme iddiası',
    prevention: 'Önleme iddiası',
    disease: 'Hastalık adı',
    drug: 'İlaç ifadesi',
    guarantee: 'Kesin sonuç vaadi',
  },
  publishBlockers: {
    product_not_active: 'Ürün etkin değil (durumu Etkin yapın).',
    vet_approval_required: 'Sağlık içeriği veteriner onayı bekliyor.',
    base_price_required: 'Mağazanın temel (perakende) listesinde fiyat yok.',
  },
  events: {
    'organization.updated': 'İşletme bilgileri değişti',
    'brand.created': 'Marka oluşturuldu',
    'brand.updated': 'Marka güncellendi',
    'store.created': 'Mağaza oluşturuldu',
    'store.updated': 'Mağaza güncellendi',
    'member.invited': 'Kullanıcı davet edildi',
    'member.updated': 'Kullanıcı rolü/durumu değişti',
    'integration.error': 'Entegrasyon hatası',
    'integration.connected': 'Entegrasyon bağlandı',
    'webhook.test': 'Test olayı',
    'product.created': 'Ürün oluşturuldu',
    'product.updated': 'Ürün güncellendi',
    'product.published': 'Ürün yayınlandı',
    'product.unpublished': 'Ürün yayından kaldırıldı',
    'price.changed': 'Fiyat değişti',
    'stock.changed': 'Stok değişti',
    'customer.created': 'Müşteri oluşturuldu',
    'dealer.applied': 'Bayilik başvurusu geldi',
    'dealer.approved': 'Bayi onaylandı',
    'order.created': 'Sipariş oluşturuldu',
    'order.status_changed': 'Sipariş durumu değişti',
    'order.updated': 'Sipariş düzenlendi',
    'order.items_cancelled': 'Siparişte ürün iptal edildi (kısmi iptal)',
    'payment.captured': 'Ödeme alındı',
    'payment.failed': 'Ödeme başarısız',
    'payment.review_required': 'Ödeme incelemeye düştü (elle karar gerekiyor)',
    'store.went_live': 'Mağaza yayına alındı',
    'return.requested': 'İade/cayma talebi açıldı',
    'privacy.requested': 'KVKK başvurusu alındı',
    'privacy.anonymized': 'KVKK silme başvurusu: kişisel veriler anonimleştirildi',
    'form.submitted': 'İletişim formundan mesaj geldi',
    'dealer.balance_paid': 'Bayi kartla cari hesap ödemesi yaptı',
    'return.completed': 'İade talebi tamamlandı',
    'theme.published': 'Tema yayımlandı',
    'page.published': 'Sayfa yayımlandı',
    'post.published': 'Blog yazısı yayımlandı',
    'refund.created': 'Para iadesi yapıldı',
    'shipment.created': 'Gönderi oluşturuldu',
    'shipment.updated': 'Gönderi durumu değişti',
    'invoice.sent': 'Fatura gönderildi',
    'legal.published': 'Yasal metin yayımlandı',
  },
} as const;

/** Anahtar yapısı tr'den türetilir; diğer diller bu yapıyı karşılar (değerler string). */
type Widen<T> = { [K in keyof T]: T[K] extends string ? string : Widen<T[K]> };
export type Messages = Widen<typeof tr>;
type DeepPartial<T> = { [K in keyof T]?: T[K] extends string ? string : DeepPartial<T[K]> };
export type PartialMessages = DeepPartial<Messages>;
